// src/shared/ui/components/unsaved-changes.tsx
// The "Unsaved changes" notice bar (Antonin, 16 Oct: instead of a floating, disabled Save, a bar under the header
// appears once something needs saving: "Unsaved changes · Cancel · Save"; no floating Saves). ONE shared piece for the
// whole app; the Encounter chart is the first host.
//
// Three parts:
//   UnsavedChangesBar       presentational: visible, onSave, onCancel, saving, error. Nothing else.
//   UnsavedChangesProvider  a host (a page body) wraps the sections that edit; it renders the bar in a reserved slot
//                           above its children and keeps the list of sections that are dirty.
//   useUnsavedChangesSource a section reports { dirty, saving, save, cancel }. It returns `hosted`: true when a
//                           provider owns the bar, so the section hides its own Save; outside a provider it is false
//                           and the section keeps its own buttons unchanged (patient-edit pages).
// With several dirty sections one bar serves all: Save runs each section's own save in turn (same validation, same
// toasts); Cancel discards all of them. Look: showcase-bridge.css `/* unsaved changes bar */`.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button } from '@scripsteam/scrips-signal-ds';

export interface UnsavedChangesBarProps {
  visible: boolean;
  onSave: () => void;
  onCancel: () => void;
  /** Save is running: the primary shows the busy label and both buttons are disabled. */
  saving?: boolean;
  /** Why the save did not go through (the form's own errors stay where they are). */
  error?: string | null;
}

export function UnsavedChangesBar({ visible, onSave, onCancel, saving = false, error = null }: UnsavedChangesBarProps) {
  if (!visible) return null;
  return (
    <div
      role="region"
      aria-label="Unsaved changes"
      aria-live="polite"
      data-slot="unsaved-changes-bar"
      className="unsaved-changes-bar"
    >
      <span className="unsaved-changes-text">Unsaved changes</span>
      {error ? (
        <span role="alert" className="unsaved-changes-error">
          {error}
        </span>
      ) : null}
      <div className="unsaved-changes-actions">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="button" size="sm" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

/** What a section hands the bar. `save` resolves true / void when it went through, false or a message when it did not. */
export interface UnsavedChangesSource {
  dirty: boolean;
  saving: boolean;
  save: () => Promise<boolean | string | void>;
  cancel: () => void;
}

interface Handlers {
  save: UnsavedChangesSource['save'];
  cancel: UnsavedChangesSource['cancel'];
}

interface Ctx {
  report: (id: string, state: { dirty: boolean; saving: boolean }) => void;
  forget: (id: string) => void;
  handlers: React.MutableRefObject<Map<string, Handlers>>;
}

const UnsavedChangesContext = createContext<Ctx | null>(null);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const [states, setStates] = useState<Record<string, { dirty: boolean; saving: boolean }>>({});
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handlers = useRef(new Map<string, Handlers>());

  const report = useCallback((id: string, state: { dirty: boolean; saving: boolean }) => {
    setStates((prev) => {
      const cur = prev[id];
      if (cur && cur.dirty === state.dirty && cur.saving === state.saving) return prev;
      return { ...prev, [id]: state };
    });
  }, []);
  const forget = useCallback((id: string) => {
    handlers.current.delete(id);
    setStates((prev) => {
      if (!(id in prev)) return prev;
      const { [id]: _gone, ...rest } = prev;
      return rest;
    });
  }, []);

  const dirtyIds = Object.keys(states).filter((id) => states[id]?.dirty);
  const visible = dirtyIds.length > 0;
  const saving = running || dirtyIds.some((id) => states[id]?.saving);

  useEffect(() => {
    if (!visible) setError(null);
  }, [visible]);

  const onSave = async () => {
    setRunning(true);
    setError(null);
    try {
      for (const id of dirtyIds) {
        const result = await handlers.current.get(id)?.save();
        if (result === false || typeof result === 'string') {
          setError(typeof result === 'string' ? result : 'Some changes could not be saved.');
          return;
        }
      }
    } finally {
      setRunning(false);
    }
  };
  const onCancel = () => {
    setError(null);
    for (const id of dirtyIds) handlers.current.get(id)?.cancel();
  };

  const ctx = useMemo<Ctx>(() => ({ report, forget, handlers }), [report, forget]);

  return (
    <UnsavedChangesContext.Provider value={ctx}>
      <UnsavedChangesBar visible={visible} onSave={() => void onSave()} onCancel={onCancel} saving={saving} error={error} />
      {children}
    </UnsavedChangesContext.Provider>
  );
}

/** Register a section with the bar above it. Returns `hosted`: hide the section's own Save when true. */
export function useUnsavedChangesSource(source: UnsavedChangesSource): boolean {
  const ctx = useContext(UnsavedChangesContext);
  const id = useId();
  if (ctx) ctx.handlers.current.set(id, { save: source.save, cancel: source.cancel });
  const report = ctx?.report;
  const forget = ctx?.forget;
  useEffect(() => {
    report?.(id, { dirty: source.dirty, saving: source.saving });
  }, [report, id, source.dirty, source.saving]);
  useEffect(() => () => forget?.(id), [forget, id]);
  return ctx !== null;
}
